import React, { useEffect, useState } from 'react';
import { Alert, Card, Typography, Button, Space, Tag, message, Modal, Input } from 'antd';
import {useNavigate, useParams} from 'react-router-dom';
import campaignLinkRequestService from "../../../../services/campaignLinkRequestService";
import RichTextViewer from "../../../../components/molecules/Forms/RichText/viewer";
import { CampaignLinkRequestObject, PromoterSocialsLinkObject } from "../../../../utils/types";

const { Text, Link } = Typography;
const { TextArea } = Input;

const formatNumber = (value?: number | null) =>
    typeof value === 'number' ? value.toLocaleString() : 'Unavailable';

const formatSubmittedFollowers = (value?: number | null) =>
    typeof value === 'number' ? value.toLocaleString() : 'Not provided';

const platformLabels: Record<string, string> = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    x: 'X',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    twitch: 'Twitch'
};

const formatPlatform = (value?: string) =>
    value ? platformLabels[value] || value.charAt(0).toUpperCase() + value.slice(1) : 'Social';

const getSocialStatusTag = (status: PromoterSocialsLinkObject['status']) => {
    const color =
        status === 'success' ? 'green' :
            status === 'unavailable' ? 'orange' :
                'red';

    return <Tag color={color}>{status}</Tag>;
};

const getSocialErrorText = (link: PromoterSocialsLinkObject) => {
    if (link.status === 'success') return null;
    return link.error || 'Follower count could not be verified for this social profile.';
};

const CampaignLinkRequestDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [request, setRequest] = useState<CampaignLinkRequestObject | null>(null);
    const [loading, setLoading] = useState(false);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const navigate = useNavigate();

    const fetchRequest = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await campaignLinkRequestService.getRequestById(id);
            setRequest(res.data.request);
        } catch (e: any) {
            message.error(e.message || 'Failed to load request');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequest();
    }, [id]);

    const handleApprove = async () => {
        if (!id) return;
        try {
            await campaignLinkRequestService.approveRequest(id);
            message.success('Request approved successfully');
            fetchRequest();
        } catch (e: any) {
            message.error(e.message || 'Failed to approve request');
        }
    };

    const handleReject = async () => {
        if (!id) return;
        try {
            await campaignLinkRequestService.rejectRequest(id, { rejectionReason });
            message.success('Request rejected successfully');
            setRejectOpen(false);
            setRejectionReason('');
            fetchRequest();
        } catch (e: any) {
            message.error(e.message || 'Failed to reject request');
        }
    };

    if (!request) {
        return <p>{loading ? 'Loading request...' : 'Request not found.'}</p>;
    }

    const promoterSocials = request.promoterSocialsId;
    const campaignTitle = typeof request.campaignId === 'object' ? request.campaignId?.title : undefined;
    const promoterUsername = typeof request.promoterId === 'object' ? request.promoterId?.username : undefined;
    const promoterEmail = typeof request.promoterId === 'object' ? request.promoterId?.email : undefined;

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
            <label className="title">Affiliate request</label>

            <Card style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <Text strong>Campaign:</Text> {campaignTitle}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <Text strong>Promoter:</Text> {promoterUsername}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <Text strong>Email:</Text> {promoterEmail}
                </div>

                <div style={{ marginBottom: 12 }}>
                    <Text strong>Submitted followers:</Text> {formatSubmittedFollowers(request.numberOfFollowers)}
                </div>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Verify promoter ownership"
                    description="Promotify validates follower counts from the submitted social links. Before approving, confirm the attached profiles belong to this promoter."
                />

                {promoterSocials && (
                    <div style={{ marginBottom: 12 }}>
                        <Text strong>Scraped followers:</Text> {formatNumber(promoterSocials.scrapedFollowers)}
                    </div>
                )}

                {promoterSocials && (
                    <div style={{ marginBottom: 12 }}>
                        <Text strong>Social scrape results:</Text>
                        <div style={{ marginTop: 8 }}>
                            {(promoterSocials.links || []).map((link) => {
                                const errorText = getSocialErrorText(link);

                                return (
                                    <Card key={`${link.socialMediaName}-${link.profileLink}`} size="small" style={{ marginBottom: 8 }}>
                                        <div style={{ marginBottom: 6 }}>
                                            <Space wrap>
                                                <Text strong>{formatPlatform(link.socialMediaName)}</Text>
                                                {getSocialStatusTag(link.status)}
                                            </Space>
                                        </div>

                                        <div style={{ marginBottom: 6 }}>
                                            <Link href={link.profileLink} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                                                {link.profileLink}
                                            </Link>
                                        </div>

                                        <div style={{ marginBottom: errorText ? 6 : 0 }}>
                                            <Text type="secondary">Followers:</Text> {formatNumber(link.followers)}
                                        </div>

                                        {errorText && (
                                            <Text type="secondary">{errorText}</Text>
                                        )}
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!promoterSocials && (
                    <div style={{ marginBottom: 12 }}>
                        <Text strong>Social links:</Text>
                        <div style={{ marginTop: 8 }}>
                            {(request.links || []).map((link, index) => (
                                <div key={index} style={{ marginBottom: 6 }}>
                                    <Text strong>{link.type}:</Text>{' '}
                                    <Link href={link.url} target="_blank" rel="noopener noreferrer">
                                        {link.url}
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{ marginBottom: 12 }}>
                    <Text strong>Status:</Text>{' '}
                    <Tag color={
                        request.status === 'pending' ? 'orange' :
                            request.status === 'approved' ? 'green' :
                                'red'
                    }>
                        {request.status}
                    </Tag>
                </div>

                <div style={{ marginBottom: 12 }}>
                    <Text strong>Bio:</Text>
                    <div className="rtx-viewer" style={{ marginTop: 6 }}>
                        <RichTextViewer html={request.bio || ''} />
                    </div>
                </div>

                <Space style={{ marginTop: 16 }}>
                    {request.status === 'pending' && (
                        <>
                            <Button type="primary" onClick={handleApprove}>
                                Approve
                            </Button>
                            <Button danger onClick={() => setRejectOpen(true)}>
                                Reject
                            </Button>
                        </>
                    )}
                    <Button onClick={() => navigate('/affiliate-requests')}>
                        Back
                    </Button>
                </Space>
            </Card>

            <Modal
                open={rejectOpen}
                title="Reject affiliate request"
                onCancel={() => setRejectOpen(false)}
                onOk={handleReject}
            >
                <TextArea
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Optional reason"
                />
            </Modal>
        </div>
    );
};

export default CampaignLinkRequestDetailPage;
